import cn from 'classnames';
import throttle from 'lodash/throttle';
import { useEffect, useRef, useState, useCallback } from 'react';
import { ScrollTo } from 'react-scroll-to';
import { CSSTransition } from 'react-transition-group';

import ScrollArrows from './ScrollArrows';

const THROTTLE_MS = 300;

const ScrollableList = ({
  onScrollStateChange = null,
  showArrows = true,
  buttonStyles,
  iconStyles,
  children,
  numSlides,
  slidesContainerStyles,
  transitionRef,
}) => {
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(true);
  const listRef = useRef();
  const containerRef = useRef(null);

  useEffect(() => {
    const updateShowState = throttle(() => {
      if (containerRef.current) {
        const { offsetWidth, scrollWidth, scrollLeft } = containerRef.current;
        setShowPrev(scrollLeft > 0);
        setShowNext(scrollLeft + offsetWidth < scrollWidth);
      }
    }, THROTTLE_MS);

    updateShowState();

    window.addEventListener('resize', updateShowState);
    containerRef.current?.addEventListener('scroll', updateShowState);

    return () => {
      window.removeEventListener('resize', updateShowState);
      containerRef.current?.removeEventListener('scroll', updateShowState);
      updateShowState.cancel();
    };
  }, [numSlides]);

  useEffect(() => {
    if (onScrollStateChange) {
      onScrollStateChange({
        scrollLeft,
        scrollRight,
        showPrev,
        showNext,
      });
    }
  }, [showPrev, showNext, scrollLeft, scrollRight, onScrollStateChange]);

  const getCardWidth = () => {
    if (!containerRef.current?.children[0]) return;

    const firstCard = containerRef.current.children[0];
    const cardWidth = firstCard.getBoundingClientRect().width;

    let gap = 16; // Default for gap-4

    // If there's a second card, measure the actual gap
    if (containerRef.current.children[1]) {
      const secondCard = containerRef.current.children[1];
      gap = secondCard.getBoundingClientRect().left - firstCard.getBoundingClientRect().right;
    }

    return cardWidth + gap;
  };

  const scrollValue = useCallback(() => {
    const mealCardWidth = getCardWidth();
    return Math.floor(containerRef.current.offsetWidth / mealCardWidth) * mealCardWidth;
  }, []);

  const updateScrollPosition = useCallback(scrollChange => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: scrollChange,
        behavior: 'smooth',
      });
    }
  }, []);

  const scrollLeft = useCallback(() => {
    updateScrollPosition(-scrollValue());
  }, [updateScrollPosition, scrollValue]);

  const scrollRight = useCallback(() => {
    updateScrollPosition(scrollValue());
  }, [updateScrollPosition, scrollValue]);

  return (
    <CSSTransition timeout={200} classNames="fadeInOut" nodeRef={listRef}>
      <div ref={listRef}>
        <ScrollTo>
          {({ scrollTo }) => (
            <div ref={transitionRef} className="relative">
              <ul
                ref={containerRef}
                className={cn(
                  'scrollbar-hide flex list-none overflow-x-auto',
                  slidesContainerStyles
                )}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {children}
              </ul>
              {showArrows && (
                <ScrollArrows
                  iconStyles={iconStyles}
                  buttonStyles={buttonStyles}
                  scrollLeft={scrollLeft}
                  scrollRight={scrollRight}
                  showPrev={showPrev}
                  showNext={showNext}
                  scrollTo={scrollTo}
                />
              )}
            </div>
          )}
        </ScrollTo>
      </div>
    </CSSTransition>
  );
};

ScrollableList.propTypes = {
  buttonStyles: PropTypes.string,
  iconStyles: PropTypes.string,
  children: PropTypes.node.isRequired,
  compressChildren: PropTypes.bool,
  numSlides: PropTypes.number.isRequired,
  onScrollStateChange: PropTypes.func,
  showArrows: PropTypes.bool,
  slidesContainerStyles: PropTypes.string,
  transitionRef: PropTypes.object,
};

export default ScrollableList;
